import { InstituteDetails } from '@admin/api/apiTypes';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React from 'react';

type FeatureField = 'vcm_enabled' | 'sso_enabled';

interface Feature {
  field: FeatureField;
  label: string;
  description: string;
}

const FEATURES: Feature[] = [
  {
    field: 'vcm_enabled',
    label: 'VCM',
    description:
      'Enable the VCM (Vocational Competency Map) functionality in the Educator and Admin Apps. ' +
      'To use the VCM feature Course, Unit and Qualification data must be uploaded to the system via CSV.'
  },
  {
    field: 'sso_enabled',
    label: 'SSO',
    description:
      'Enable SSO logins via a Cognito/Microsoft Entra ID integration. ' +
      'To use this feature infrastructure configuration is required first.'
  }
];

interface FeaturesProps {
  instituteDetails: InstituteDetails;
  onChange: (institute: InstituteDetails) => void;
}

// Super admin only. Changes are held in the parent's institute details and
// saved with the page's Save button, which reloads the page so the header
// nav and Institute tabs pick up the newly enabled features.
const Features = ({ instituteDetails, onChange }: FeaturesProps) => {
  const theme = useTheme();

  const handleToggle = (field: FeatureField) => (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...instituteDetails, [field]: event.target.checked });
  };

  const cellSx = {
    borderRight: `1px solid ${theme.palette.divider}`,
    '&:last-of-type': { borderRight: 'none' }
  };

  return (
    <Paper>
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          gap: 1,
          padding: theme.typography.pxToRem(16)
        }}
      >
        <InfoOutlineIcon fontSize="small" />
        <Typography>Enable features for this institute (Super admin only)</Typography>
      </Box>
      <Table>
        <TableBody>
          {FEATURES.map(({ field, label, description }) => (
            <TableRow key={field} sx={{ '&:last-child td': { borderBottom: 'none' } }}>
              <TableCell sx={{ ...cellSx, width: '20%' }}>
                <Typography variant="body2">{label}</Typography>
              </TableCell>
              <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap', width: '1%' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!instituteDetails[field]}
                      name={field}
                      onChange={handleToggle(field)}
                    />
                  }
                  label="Enable"
                />
              </TableCell>
              <TableCell sx={cellSx}>
                <Typography variant="body2">{description}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default Features;
